import React, { useEffect, useRef, useState } from "react";
import {
  FaLightbulb,
  FaLink,
  FaExclamationCircle,
  FaBolt,
  FaEdit,
} from "react-icons/fa";
import styles from "../../pages/Monitor/Monitor.module.css";

const normalizeVisibleValue = (value) => {
  if (value === undefined || value === null) return true;

  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE"
  );
};

const normalizeDailyTimeValue = (value, fallback = "00:00") => {
  const raw = String(value ?? "").trim();

  const match = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);

  if (!match) {
    return fallback;
  }

  const hour = String(Number(match[1])).padStart(2, "0");
  const minute = String(Number(match[2])).padStart(2, "0");

  return `${hour}:${minute}`;
};

const timeInputStyle = {
  width: "72px",
  direction: "ltr",
  unicodeBidi: "isolate",
  textAlign: "center",
};

const DigitalOutputCard = ({
  type,
  index,
  label,
  icon,
  data,
  onUpdate,
  customNames = {},
  onSaveCustomName,

  showVisibilityToggle = false,
  isVisibleInMonitor = true,
  onVisibilityChange,
  canConfigure = true,
  canControlOutputs = true,
  readOnlyPreview = false,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  const [localVisibleInMonitor, setLocalVisibleInMonitor] = useState(
    normalizeVisibleValue(isVisibleInMonitor),
  );
  const [isSavingVisibility, setIsSavingVisibility] = useState(false);

  const [isLogicMenuOpen, setIsLogicMenuOpen] = useState(false);
  const [isTimerModeMenuOpen, setIsTimerModeMenuOpen] = useState(false);
  const logicMenuRef = useRef(null);
  const timerModeMenuRef = useRef(null);

  useEffect(() => {
    setLocalVisibleInMonitor(normalizeVisibleValue(isVisibleInMonitor));
  }, [isVisibleInMonitor]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        logicMenuRef.current &&
        !logicMenuRef.current.contains(event.target)
      ) {
        setIsLogicMenuOpen(false);
      }

      if (
        timerModeMenuRef.current &&
        !timerModeMenuRef.current.contains(event.target)
      ) {
        setIsTimerModeMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsLogicMenuOpen(false);
        setIsTimerModeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleVisibilityToggle = async (e) => {
    if (isReadOnlyPreview) return;
    const nextValue = e.target.checked;
    const previousValue = localVisibleInMonitor;

    setLocalVisibleInMonitor(nextValue);

    if (!onVisibilityChange) return;

    try {
      setIsSavingVisibility(true);

      const result = onVisibilityChange(nextValue);

      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (error) {
      console.error("خطا در تغییر وضعیت نمایش در مانیتورینگ:", error);
      setLocalVisibleInMonitor(previousValue);
      alert("خطا در ذخیره وضعیت نمایش");
    } finally {
      setIsSavingVisibility(false);
    }
  };

  if (!data) return null;

  const isReadOnlyPreview = Boolean(
    readOnlyPreview ||
    data?.isOfflinePreview ||
    data?.isLastStatePreview ||
    typeof onUpdate !== "function" ||
    !canConfigure ||
    !canControlOutputs,
  );

  const safeUpdate = (prop, value) => {
    if (isReadOnlyPreview || typeof onUpdate !== "function") return undefined;
    return onUpdate(prop, value);
  };

  const { status, settings = {}, triggered, causedBy, error } = data;

  const isOn = status === "1";

  // دریافت نام سفارشی برای این خروجی خاص
  const customName = customNames[index] ?? customNames[String(index)];

  const logicModes = ["OR (یا)", "AND (و)", "NOR", "NAND", "XOR", "XNOR"];
  const selectedLogicIndex = Number(settings.logicMode ?? 0);
  const selectedLogicLabel = logicModes[selectedLogicIndex] || logicModes[0];

  const timerModes = [
    { value: "0", label: "غیرفعال" },
    { value: "1", label: "تاخیر وصل" },
    { value: "2", label: "تاخیر قطع" },
    { value: "3", label: "دوره‌ای" },
    { value: "4", label: "روزانه" },
  ];
  const selectedTimerModeValue = String(settings.timerMode ?? "0");
  const selectedTimerMode =
    timerModes.find((item) => item.value === selectedTimerModeValue) ||
    timerModes[0];

  const showDelayOnField = selectedTimerModeValue === "1";
  const showDelayOffField = selectedTimerModeValue === "2";
  const showPeriodicFields = selectedTimerModeValue === "3";
  const showDailyFields = selectedTimerModeValue === "4";

  const dailyOnValue = normalizeDailyTimeValue(settings.dailyOn);
  const dailyOffValue = normalizeDailyTimeValue(settings.dailyOff);

  const handleDailyTimeBlur = (prop, previousValue) => (e) => {
    const previousNormalized = normalizeDailyTimeValue(previousValue);
    const nextNormalized = normalizeDailyTimeValue(
      e.currentTarget.value,
      previousNormalized,
    );

    e.currentTarget.value = nextNormalized;

    if (nextNormalized !== previousNormalized) {
      safeUpdate(prop, nextNormalized);
    }
  };

  const handleLogicModeSelect = (logicIndex) => {
    safeUpdate("logicMode", String(logicIndex));
    setIsLogicMenuOpen(false);
  };

  const handleTimerModeSelect = (modeValue) => {
    safeUpdate("timerMode", String(modeValue));
    setIsTimerModeMenuOpen(false);
  };

  const handleSaveName = () => {
    if (!isReadOnlyPreview && onSaveCustomName) {
      onSaveCustomName(index, editedName.trim());
    }

    setIsEditingName(false);
  };

  const startEditingName = (e) => {
    e.stopPropagation();
    if (isReadOnlyPreview) return;
    setEditedName(customName || "");
    setIsEditingName(true);
  };

  return (
    <div
      className={`${styles.sensorCard} ${isReadOnlyPreview ? styles.configOfflinePreviewCard : ""}`}
      style={{
        borderRight: "4px solid #39ff14",
        position: "relative",
        overflow: "visible",
        zIndex: isLogicMenuOpen || isTimerModeMenuOpen ? 3000 : 1,
      }}
    >
      {isReadOnlyPreview && (
        <div className={styles.configOfflinePreviewTag}>
          آخرین کانفیگ ذخیره‌شده
        </div>
      )}
      {/* --- هدر (با قابلیت ویرایش نام) --- */}
      <div className={styles.cardHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {icon}

          <span style={{ fontWeight: "bold" }}>
            {customName ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <span style={{ fontSize: "14px", color: "#f1c40f" }}>
                  {customName}
                </span>

                <span style={{ fontSize: "10px", color: "#7f8c8d" }}>
                  {label}
                </span>
              </div>
            ) : (
              label
            )}
          </span>
        </div>

        {/* دکمه ویرایش نام */}
        <div
          onClick={startEditingName}
          style={{ cursor: "pointer", padding: "5px", color: "#3498db" }}
          title="ویرایش نام خروجی"
        >
          <FaEdit style={{ opacity: isReadOnlyPreview ? 0.35 : 1 }} />
        </div>
      </div>

      {/* مدال ویرایش نام */}
      {isEditingName && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#2c3e50",
            padding: "15px",
            borderRadius: "8px",
            zIndex: 100,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            border: "1px solid rgba(255,255,255,0.1)",
            minWidth: "200px",
          }}
        >
          <div
            style={{ marginBottom: "10px", fontSize: "12px", color: "#ccc" }}
          >
            ویرایش نام خروجی {index}
          </div>

          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            placeholder="مثلاً: پمپ آب"
            style={{
              width: "100%",
              padding: "8px 5px",
              borderRadius: "4px",
              border: "1px solid #34495e",
              backgroundColor: "#1a252f",
              color: "#f1c40f",
              marginBottom: "10px",
            }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveName();
              if (e.key === "Escape") setIsEditingName(false);
            }}
          />

          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              onClick={() => setIsEditingName(false)}
              style={{
                padding: "5px 10px",
                backgroundColor: "#7f8c8d",
                border: "none",
                borderRadius: "4px",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              لغو
            </button>

            <button
              onClick={handleSaveName}
              style={{
                padding: "5px 10px",
                backgroundColor: "#2ecc71",
                border: "none",
                borderRadius: "4px",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              ذخیره
            </button>
          </div>
        </div>
      )}

      {/* --- وضعیت فعلی خروجی --- */}
      <div className={styles.centerStatus}>
        <FaBolt
          className={`${styles.powerIcon} ${isOn ? styles.powerOn : styles.powerOff}`}
        />

        <div className={styles.statusText}>
          {isOn ? "خروجی فعال" : "خروجی غیرفعال"}
        </div>

        {causedBy && data.connected !== false && (
          <div
            style={{
              fontSize: "10px",
              color: "#52ae47",
              marginTop: "5px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <FaLink /> توسط: {causedBy}
          </div>
        )}
      </div>

      {/* --- گروه تنظیمات --- */}
      <div
        className={styles.settingsGroup}
        style={{ position: "relative", overflow: "visible" }}
      >
        {triggered && <div className={styles.triggerGlow}></div>}

        <div className={styles.triggerIconContainer}>
          <FaLightbulb
            className={`${styles.trigIcon} ${triggered ? styles.trigOn : styles.trigOff}`}
            style={{ fontSize: "18px", transform: "rotate(180deg)" }}
          />

          {triggered && <div className={styles.blinkingTooltip}>Trig فعال</div>}
        </div>

        {showVisibilityToggle && (
          <div className={styles.settingRow}>
            <span>
              نمایش در مانیتورینگ
              {isSavingVisibility && (
                <small style={{ marginRight: "6px", color: "#95a5a6" }}>
                  در حال ذخیره...
                </small>
              )}
            </span>

            <label
              className={styles.toggleSwitch}
              title={
                localVisibleInMonitor
                  ? "در مانیتورینگ نمایش داده می‌شود"
                  : "در مانیتورینگ مخفی است"
              }
            >
              <input
                type="checkbox"
                checked={localVisibleInMonitor}
                disabled={isSavingVisibility || isReadOnlyPreview}
                onChange={handleVisibilityToggle}
              />

              <span className={styles.slider}></span>
            </label>
          </div>
        )}

        <div className={styles.settingRow}>
          <span>فعالسازی (Enable):</span>

          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              checked={settings.enable === "1"}
              disabled={isReadOnlyPreview}
              onChange={(e) =>
                safeUpdate("enable", e.target.checked ? "1" : "0")
              }
            />

            <span className={styles.slider}></span>
          </label>
        </div>

        {error && (
          <div
            style={{ color: "#ff4d4d", fontSize: "9px", marginBottom: "10px" }}
          >
            <FaExclamationCircle /> خطا: وابسته به {error}
          </div>
        )}

        <div
          className={styles.settingRow}
          style={{ position: "relative", zIndex: isLogicMenuOpen ? 80 : 20 }}
        >
          <span>منطق (Logic):</span>

          <div
            ref={logicMenuRef}
            style={{ position: "relative", zIndex: isLogicMenuOpen ? 90 : 30 }}
            className={`
                            ${styles.logicSelectWrap}
                            ${isLogicMenuOpen ? styles.logicSelectOpen : ""}
                        `}
          >
            <button
              type="button"
              className={styles.logicSelectButton}
              onClick={(e) => {
                e.stopPropagation();
                if (!isReadOnlyPreview) {
                  setIsTimerModeMenuOpen(false);
                  setIsLogicMenuOpen((prev) => !prev);
                }
              }}
              disabled={isReadOnlyPreview}
              aria-haspopup="listbox"
              aria-expanded={isLogicMenuOpen}
            >
              <span>{selectedLogicLabel}</span>
              <span className={styles.logicSelectArrow}>▾</span>
            </button>

            {isLogicMenuOpen && (
              <div
                className={styles.logicSelectMenu}
                role="listbox"
                style={{ zIndex: 1000 }}
              >
                {logicModes.map((text, i) => (
                  <button
                    key={i}
                    type="button"
                    role="option"
                    aria-selected={selectedLogicIndex === i}
                    className={`
                                            ${styles.logicSelectOption}
                                            ${selectedLogicIndex === i ? styles.logicSelectOptionActive : ""}
                                        `}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogicModeSelect(i);
                    }}
                  >
                    {text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className={styles.settingRow}
          style={{
            position: "relative",
            zIndex: isTimerModeMenuOpen ? 70 : 10,
          }}
        >
          <span>حالت تایمر:</span>

          <div
            ref={timerModeMenuRef}
            style={{
              position: "relative",
              zIndex: isTimerModeMenuOpen ? 80 : 10,
            }}
            className={`
                            ${styles.logicSelectWrap}
                            ${isTimerModeMenuOpen ? styles.logicSelectOpen : ""}
                        `}
          >
            <button
              type="button"
              className={styles.logicSelectButton}
              onClick={(e) => {
                e.stopPropagation();
                if (!isReadOnlyPreview) {
                  setIsLogicMenuOpen(false);
                  setIsTimerModeMenuOpen((prev) => !prev);
                }
              }}
              disabled={isReadOnlyPreview}
              aria-haspopup="listbox"
              aria-expanded={isTimerModeMenuOpen}
            >
              <span>{selectedTimerMode.label}</span>
              <span className={styles.logicSelectArrow}>▾</span>
            </button>

            {isTimerModeMenuOpen && (
              <div
                className={styles.logicSelectMenu}
                role="listbox"
                style={{ zIndex: 1000 }}
              >
                {timerModes.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="option"
                    aria-selected={selectedTimerModeValue === item.value}
                    className={`
                                            ${styles.logicSelectOption}
                                            ${selectedTimerModeValue === item.value ? styles.logicSelectOptionActive : ""}
                                        `}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTimerModeSelect(item.value);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {showDelayOnField && (
          <div className={styles.settingRow}>
            <span>تاخیر در وصل:</span>

            <input
              className={styles.inputNum}
              style={{ width: "72px" }}
              defaultValue={settings.delayOn}
              disabled={isReadOnlyPreview}
              onBlur={(e) => safeUpdate("delayOn", e.target.value)}
            />
          </div>
        )}

        {showDelayOffField && (
          <div className={styles.settingRow}>
            <span>تاخیر در قطع:</span>

            <input
              className={styles.inputNum}
              style={{ width: "72px" }}
              defaultValue={settings.delayOff}
              disabled={isReadOnlyPreview}
              onBlur={(e) => safeUpdate("delayOff", e.target.value)}
            />
          </div>
        )}

        {showPeriodicFields && (
          <>
            <div className={styles.settingRow}>
              <span>روشن (ثانیه):</span>

              <input
                className={styles.inputNum}
                style={{ width: "72px" }}
                defaultValue={settings.periodicOn}
                disabled={isReadOnlyPreview}
                onBlur={(e) => safeUpdate("periodicOn", e.target.value)}
              />
            </div>

            <div className={styles.settingRow}>
              <span>خاموش (ثانیه):</span>

              <input
                className={styles.inputNum}
                style={{ width: "72px" }}
                defaultValue={settings.periodicOff}
                disabled={isReadOnlyPreview}
                onBlur={(e) => safeUpdate("periodicOff", e.target.value)}
              />
            </div>
          </>
        )}

        {showDailyFields && (
          <div className={styles.settingRow}>
            <span>تایمر روزانه:</span>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                justifyContent: "flex-end",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  color: "#cbd5e1",
                }}
              >
                <span>از ساعت</span>
                <input
                  type="time"
                  step="60"
                  className={styles.inputNum}
                  style={timeInputStyle}
                  defaultValue={dailyOnValue}
                  disabled={isReadOnlyPreview}
                  onBlur={handleDailyTimeBlur("dailyOn", settings.dailyOn)}
                  title="از ساعت"
                  aria-label="از ساعت"
                />
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  color: "#cbd5e1",
                }}
              >
                <span>تا ساعت</span>
                <input
                  type="time"
                  step="60"
                  className={styles.inputNum}
                  style={timeInputStyle}
                  defaultValue={dailyOffValue}
                  disabled={isReadOnlyPreview}
                  onBlur={handleDailyTimeBlur("dailyOff", settings.dailyOff)}
                  title="تا ساعت"
                  aria-label="تا ساعت"
                />
              </label>
            </div>
          </div>
        )}

        <div className={styles.settingRow}>
          <span>حالت معکوس:</span>

          <label className={styles.toggleSwitch} title="Reverse">
            <input
              type="checkbox"
              checked={settings.reverse === "1"}
              disabled={isReadOnlyPreview}
              onChange={(e) =>
                safeUpdate("reverse", e.target.checked ? "1" : "0")
              }
            />

            <span className={styles.slider}></span>
          </label>
        </div>

        <div className={styles.settingRow}>
          <span>پیامک:</span>

          <label className={styles.toggleSwitch} title="SMS">
            <input
              type="checkbox"
              checked={settings.alertSms === "1"}
              disabled={isReadOnlyPreview}
              onChange={(e) =>
                safeUpdate("alertSms", e.target.checked ? "1" : "0")
              }
            />

            <span className={styles.slider}></span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default DigitalOutputCard;
